import { isDefined, toError } from "@clipboard-health/util-ts";
import { type ShiftTimeProposalMessageProps } from "@src/appV2/Chat/type";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { ShiftTimeProposalMessageCard } from "@src/appV2/redesign/Chat/ShiftTimeProposalMessageCard";
import { useGetShift } from "@src/appV2/Shifts/Shift/api/useGetShift";
import {
  useGetShiftTimeProposal,
  useUpdateShiftTimeProposal,
} from "@src/appV2/Shifts/ShiftTimeProposals/api/useShiftTimeProposals";
import { isObject, isString } from "lodash";

import { ChatWidgetWrapper } from "../../redesign/Chat/ChatWidgetWrapper";
import { ShiftTimeProposalMessageCardSkeleton } from "../../redesign/Chat/ShiftTimeProposalMessageCardSkeleton";

interface OriginalShiftTimeMessageData {
  originalStartTime: string;
  originalEndTime: string;
}

function hasOriginalShiftTime(data: unknown): data is OriginalShiftTimeMessageData {
  return (
    isObject(data) &&
    "originalStartTime" in data &&
    "originalEndTime" in data &&
    isString(data.originalStartTime) &&
    isString(data.originalEndTime)
  );
}

export function ShiftTimeProposalMessage(props: ShiftTimeProposalMessageProps) {
  const { message } = props;
  const { data } = message;
  // data contains single quotes, remove them before parsing
  const messageData: unknown = isDefined(data) ? JSON.parse(data.replace(/'/g, '"')) : undefined;
  const shiftTimeProposalId: string | undefined =
    typeof messageData === "object" &&
    isDefined(messageData) &&
    "shiftTimeProposalId" in messageData &&
    typeof messageData.shiftTimeProposalId === "string"
      ? messageData.shiftTimeProposalId
      : undefined;

  const {
    data: shiftTimeProposal,
    isLoading: isLoadingShiftTimeProposal,
    isError: isErrorShiftTimeProposal,
    error: errorShiftTimeProposal,
    refetch: refetchShiftTimeProposal,
  } = useGetShiftTimeProposal(
    { shiftTimeProposalId: shiftTimeProposalId ?? "" },
    { enabled: isDefined(shiftTimeProposalId) }
  );
  const shiftTimeProposalErrorMessage = isDefined(errorShiftTimeProposal)
    ? toError(errorShiftTimeProposal).message
    : undefined;

  const {
    mutateAsync: updateShiftTimeProposalApi,
    isLoading: isUpdateShiftTimeProposalLoading,
    variables: updateShiftTimeProposalRequest,
  } = useUpdateShiftTimeProposal({ shiftTimeProposalId: shiftTimeProposalId ?? "" });
  const updatedStatus = updateShiftTimeProposalRequest?.data.attributes.status;

  const shiftId = shiftTimeProposal?.data.relationships.shift.data.id;

  const {
    data: shiftResponse,
    isLoading: isLoadingShift,
    isError: isErrorShift,
    error: errorShift,
  } = useGetShift(shiftId ?? "", {
    enabled: isDefined(shiftId),
  });
  const shiftErrorMessage = isDefined(errorShift) ? toError(errorShift).message : undefined;
  const { showSuccessToast, showErrorToast } = useToast();

  if (isLoadingShiftTimeProposal || isLoadingShift) {
    return (
      <ChatWidgetWrapper>
        <ShiftTimeProposalMessageCardSkeleton />
      </ChatWidgetWrapper>
    );
  }

  if (
    !isDefined(shiftTimeProposal) ||
    !isDefined(shiftResponse) ||
    isErrorShiftTimeProposal ||
    isErrorShift
  ) {
    logError(APP_V2_APP_EVENTS.SHIFT_TIME_PROPOSAL_MESSAGE_RENDER_FAILURE, {
      error: new Error("Failed to render shift time proposal message"),
      metadata: {
        shiftTimeProposalId,
        shiftId,
        messageId: message.messageId,
        shiftTimeProposalErrorMessage,
        shiftErrorMessage,
      },
    });
    return null;
  }

  const shift = shiftResponse.response;
  const { start: shiftStartTime, end: shiftEndTime } = shift;

  // Determine original start and end times based on message data or shift data
  const originalStartTime = hasOriginalShiftTime(messageData)
    ? messageData.originalStartTime
    : shiftStartTime;

  const originalEndTime = hasOriginalShiftTime(messageData)
    ? messageData.originalEndTime
    : shiftEndTime;

  async function respondToShiftTimeProposal(status: "ACCEPTED" | "DECLINED") {
    const statusToConstants = {
      ACCEPTED: {
        actionEvent: APP_V2_APP_EVENTS.SHIFT_TIME_PROPOSAL_ACCEPTED,
        errorEvent: APP_V2_APP_EVENTS.SHIFT_TIME_PROPOSAL_ACCEPTED_ERROR,
        action: "accept",
      },
      DECLINED: {
        actionEvent: APP_V2_APP_EVENTS.SHIFT_TIME_PROPOSAL_DECLINED,
        errorEvent: APP_V2_APP_EVENTS.SHIFT_TIME_PROPOSAL_DECLINED_ERROR,
        action: "decline",
      },
    };
    try {
      await updateShiftTimeProposalApi({
        data: {
          type: "shift-time-proposal",
          id: shiftTimeProposalId ?? "",
          attributes: {
            status,
          },
        },
      });
      logEvent(statusToConstants[status].actionEvent, {
        shiftTimeProposalId,
        shiftId,
      });
      showSuccessToast(`New shift time ${status.toLowerCase()}`);
    } catch (maybeError) {
      const error = toError(maybeError);
      logEvent(statusToConstants[status].errorEvent, { error });

      showErrorToast(
        `Failed to ${statusToConstants[status].action} new shift time: ${error.message}`
      );
    }

    await refetchShiftTimeProposal();
  }

  const { facility } = shift;
  const facilityName = facility?.name;

  const proposalMessage = isDefined(facilityName)
    ? `${facilityName} has proposed a new shift time`
    : "A facility has proposed a new shift time";

  return (
    <ChatWidgetWrapper headerText={proposalMessage}>
      <ShiftTimeProposalMessageCard
        originalStartTime={originalStartTime}
        originalEndTime={originalEndTime}
        shiftTimeProposal={shiftTimeProposal}
        shift={shift}
        isUpdateShiftTimeProposalLoading={isUpdateShiftTimeProposalLoading}
        updatedStatus={updatedStatus}
        onAccept={async () => {
          await respondToShiftTimeProposal("ACCEPTED");
        }}
        onDecline={async () => {
          await respondToShiftTimeProposal("DECLINED");
        }}
      />
    </ChatWidgetWrapper>
  );
}
